import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BiUser } from 'react-icons/bi';
import { FaAngleDown, FaAngleLeft, FaAngleRight, FaAngleUp } from 'react-icons/fa';

import { useAuth } from 'contexts';
import { BoardsInterface, LeaderboardInterface, getLeaderboard, getLeaderboards, enrichLeadersWithBadges } from 'services';
import { getAllBadges } from 'services/badges.service';
import { formatPoints } from 'utils';

import AvatarComponent from '../../components/NewAvatar/index';
import AvatarById from '../../components/NewAvatar/avatarById/index';

import './style.scss';

export default function RankingPage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);

  const [boards, setBoards] = useState<LeaderboardInterface[]>([]);
  const [activeBoardIndex, setActiveBoardIndex] = useState<number>();
  const [leaderboardsIDs, setLeaderboardsIDs] = useState<string[]>();
  const [requestCounter, setRequestCounter] = useState<number>(0);
  const [allBadges, setAllBadges] = useState<any[]>([]);
  const [leadersWithBadges, setLeadersWithBadges] = useState<Record<string, any>>({});
  const [badgesReady, setBadgesReady] = useState<boolean>(false);

  const ulRef = useRef<HTMLUListElement>(null);

  const activeLeaderboard = useMemo(() => {
    return boards[activeBoardIndex || 0];
  }, [activeBoardIndex, boards]);

  const initialLoad = useCallback(async () => {
    if (!user || !!leaderboardsIDs) return;

    setLoading(true);
    setRequestCounter(0);

    const leaderboards: BoardsInterface[] = await getLeaderboards();

    if (leaderboards?.length) {
      leaderboards.sort((a, b) => {
        const orderA = a.extra?.order ?? 0;
        const orderB = b.extra?.order ?? 0;
        return orderA - orderB;
      });

      const activeLeaderboards = leaderboards.filter(x => !x.extra || !('active' in x.extra) || x.extra.active === true);

      const IDs = activeLeaderboards.map(x => x._id);

      setLeaderboardsIDs(IDs);

      const promises: any[] = [];

      IDs.forEach(id => {
        promises.push(
          getLeaderboard(id, user._id, 1000, undefined, 0)
            .then(async leaderboard => {
              setRequestCounter(prevNumber => prevNumber + 1);

              const leaderboardData = leaderboards.find(x => x._id === id);

              console.log('leaderboardData', leaderboards, leaderboardData);

              try {
                const myPosition = leaderboard.find((item: any) => item.player === user._id);

                console.log('myPosition', myPosition);

                setRequestCounter(prevNumber => prevNumber + 1);

                return {
                  leaderboard: {
                    ...leaderboardData,
                  },
                  leaders: leaderboard,
                  my_position: myPosition || null,
                };
              } catch {
                setRequestCounter(prevNumber => prevNumber + 1);
                return null;
              }
            })
            .catch(() => {
              setRequestCounter(prevNumber => prevNumber + 1);
              return null;
            }),
        );
      });

      Promise.all(promises)
        .then(response => {
          console.log('response', response);

          const _boards = response; //response.filter(x => !!x && !!x.leaders && !!x.leaders.length);

          if (_boards?.length) {
            setBoards(_boards);
            setActiveBoardIndex(0);
          }
        })
        .catch(() => null)
        .finally(() => setLoading(false));
    } else {
      return setLoading(false);
    }
  }, [leaderboardsIDs, user]);

  useEffect(() => {
    const loadBadges = async () => {
      try {
        const badges = await getAllBadges();
        setAllBadges(badges);
      } catch (error) {
        console.error('Erro ao carregar badges:', error);
      }
    };
    loadBadges();
  }, []);

  useEffect(() => {
    if (!activeLeaderboard?.leaders || activeLeaderboard.leaders.length === 0 || allBadges.length === 0) {
      setBadgesReady(false);
      return;
    }

    const loadBadges = async () => {
      try {
        const enrichedData = await enrichLeadersWithBadges(activeLeaderboard.leaders);
        setLeadersWithBadges(enrichedData);
        setBadgesReady(true);
      } catch (error) {
        console.error('Erro ao enriquecer leaders com badges:', error);
        setBadgesReady(true);
      }
    };

    loadBadges();
  }, [activeLeaderboard, allBadges]);

  const paginateIsInactive = useCallback(
    (direction: 'prev' | 'next') => {
      if (!boards || typeof activeBoardIndex !== 'number' || boards.length <= 1) return true;

      if (direction === 'prev') {
        return activeBoardIndex === 0 ? true : false;
      } else {
        return activeBoardIndex + 1 === boards.length ? true : false;
      }
    },
    [activeBoardIndex, boards],
  );

  useEffect(() => {
    if (!user || !!leaderboardsIDs) return;

    initialLoad();
  }, [user, initialLoad, leaderboardsIDs]);

  useEffect(() => {
    if (!badgesReady || !activeLeaderboard?.leaders || !ulRef.current || !user) {
      return;
    }

    const userIndex = activeLeaderboard.leaders.findIndex(leader => leader.player === user._id);

    if (userIndex === -1) return;

    const scrollToUser = () => {
      const list = ulRef.current;
      const target = list?.querySelectorAll('li')[userIndex] as HTMLElement | undefined;

      if (!list || !target) return;

      const listRect = list.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetOffsetWithinList = targetRect.top - listRect.top + list.scrollTop;

      list.scrollTop = targetOffsetWithinList - list.clientHeight / 2 + target.clientHeight / 2;
    };

    requestAnimationFrame(scrollToUser);
  }, [badgesReady, activeLeaderboard, user]);

  function renderLoading() {
    return (
      <>
        <p className="loading text-2xl lg:text-4xl text-center bg-white/80 shadow-xl p-6 lg:p-12 rounded-xl backdrop-blur-md">
          <span className="block mb-3 text-base lg:text-lg">Carregando...</span>
          {!!leaderboardsIDs ? Math.round((requestCounter * 100) / (leaderboardsIDs.length * 2)) : '0'}%
        </p>
        {/* <ArticlePlaceholder /> */}
      </>
    );
  }

  function noResult() {
    return (
      <h2 className="text-xl lg:text-3xl bg-white/80 shadow-xl p-4 rounded-xl backdrop-blur-md">
        Não existem competições em andamento
      </h2>
    );
  }

  const renderList = useCallback(() => {
    console.log('boards', boards);
    if (!boards || !boards.length) {
      return noResult();
    }

    if (!badgesReady) {
      return renderLoading();
    }

    const showMyPosition = () => {
      if (!activeLeaderboard?.my_position) return null;

      return (
        <div className="my-position flex items-center justify-between bg-gray-50 rounded-2xl w-full py-3 px-6 lg:px-11 mb-5 text-gray-900">
          <p className="position">
            <span>Posição</span>
            {activeLeaderboard.my_position.position}
          </p>
          <div className="user flex flex-col items-center justify-center">
            <span
              className={`img ${
                !!user?.extra?.avatar || !!user?.image?.medium?.url ? 'has-image' : 'no-image'
              } w-9 lg:w-16 h-9 lg:h-16 flex items-center justify-center rounded-full overflow-hidden border border-white border-solid mb-1.5`}
            >
              {!!user?.extra?.avatar ? (
                <AvatarComponent size={64} />
              ) : !!user?.image?.medium?.url ? (
                <img src={user.image.medium.url} alt={user.name} className="h-full w-full block object-cover" />
              ) : (
                <BiUser className="w-full h-full bg-white" />
              )}
            </span>
            <p>{user?.name}</p>
          </div>

          <p className="rate">
            <span>TOTAL</span>
            {formatPoints(activeLeaderboard.my_position.total)}
          </p>
        </div>
      );
    };

    return (
      <div className="boards backdrop-blur-md flex flex-col py-0 w-full h-full">
        <h2 className="text-xl lg:text-3xl mb-5 lg:mb-8">Ranking</h2>

        <div className="head  pb-4 lg:pb-6 w-full lg:pr-4 mx-auto">
          <div className="board-title flex flex-col items-center justify-start">
            {boards.length > 1 ? (
              <div>
                <select
                  className="text-2xl border-[1px] border-black rounded p-2 mb-2 custom-select"
                  value={activeBoardIndex}
                  onChange={e => setActiveBoardIndex(Number(e.target.value))}
                >
                  {boards?.map((board, index) => (
                    <option key={index} value={index}>
                      {board.leaderboard.title}
                    </option>
                  ))}
                </select>
                <p className="not-italic font-normal text-sm lg:text-base text-center">
                  {activeLeaderboard?.leaderboard.description}
                </p>
              </div>
            ) : (
              <div>
                <h4 className="not-italic text-xl lg:text-2xl font-bold text-center mb-1">
                  {activeLeaderboard?.leaderboard.title}
                </h4>
                {!!activeLeaderboard?.leaderboard.description && (
                  <p className="not-italic font-normal text-sm lg:text-base text-center">
                    {activeLeaderboard?.leaderboard.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {!activeLeaderboard?.leaders.length ? (
          <div className={`list flex-1 w-full lg:pr-4 flex items-center justify-center`}>{noResult()}</div>
        ) : (
          <div className={`list ${!!showMyPosition() ? 'has-position' : 'no-position'} flex-1 w-full lg:pr-4`}>
            {showMyPosition()}

            <ul ref={ulRef} className="leaders-list bg-gray-50 rounded-2xl w-full px-2 lg:px-4 py-3 lg:py-5">
              {activeLeaderboard?.leaders.map(leader => (
                <li key={leader._id}>
                  <div
                    className={`${
                      leader.player === user?._id ? 'active text-white' : 'text-gray-900'
                    } flex items-center justify-between py-3 px-5 lg:px-6 rounded-full`}
                  >
                    <p className="position font-bold flex flex-col justify-center items-center">
                      <span>Posição</span>
                      {leader.move === 'up' && <FaAngleUp className="up" />}
                      {leader.position}
                      {leader.move === 'down' && <FaAngleDown className="down" />}
                    </p>

                    <div className="user flex items-center justify-start flex-1">
                      <span className="img w-9 h-9 flex items-center justify-center rounded-full overflow-hidden border border-white border-solid mr-4 text-gray-900">
                        <AvatarById id={leader.player} size={36} />
                      </span>
                      <div className="flex items-center gap-2">
                        <p>{leader.name}</p>
                        {leadersWithBadges[leader.player]?.achievements &&
                          leadersWithBadges[leader.player].achievements.length > 0 && (
                            <div className="flex gap-1">
                              {leadersWithBadges[leader.player].achievements.map((achievement: any, idx: number) => {
                                const badge = allBadges.find(b => b._id === achievement.item);
                                return (
                                  <div key={idx} className="flex items-center justify-center" title={badge?.challenge || 'Badge'}>
                                    {badge?.badge?.small?.url ? (
                                      <img
                                        src={badge.badge.small.url}
                                        alt={badge.challenge || 'Badge'}
                                        className="w-6 h-6 object-contain rounded-full"
                                      />
                                    ) : (
                                      <div className="w-6 h-6 bg-gray-300 rounded flex items-center justify-center text-xs">
                                        ★
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    </div>

                    <p className="rate flex flex-col justify-center items-center">
                      <span>TOTAL</span>
                      {formatPoints(leader.total)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLeaderboard, paginateIsInactive, boards, user, badgesReady, leadersWithBadges, allBadges]);

  return (
    <section id="ranking-wrapper" className="rounded-3xl flex flex-row items-center justify-center">
      {loading ? renderLoading() : renderList()}
    </section>
  );
}
